import { useState } from 'react';
import { FileText, Download, Trash2, Eye, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase, getSignedUrl } from '@/lib/supabase';
import { isImage, isPDF } from '@/lib/imageCompression';
import { formatDate } from '@/lib/dateUtils';
import type { FileRecord } from '@/types';

interface FilePreviewProps {
  file: FileRecord;
  canDelete?: boolean;
  onDeleted?: () => void;
}

export function FilePreview({ file, canDelete = false, onDeleted }: FilePreviewProps) {
  const [opening, setOpening] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const open = async () => {
    setOpening(true);
    const url = await getSignedUrl(file.storage_path);
    setOpening(false);
    if (!url) { toast.error('Could not get file URL'); return; }
    window.open(url, '_blank', 'noopener');
  };

  const download = async () => {
    const url = await getSignedUrl(file.storage_path);
    if (!url) { toast.error('Could not get file URL'); return; }
    const a = document.createElement('a');
    a.href = url;
    a.download = file.file_name;
    a.click();
  };

  const del = async () => {
    if (!confirm(`Delete "${file.file_name}"?`)) return;
    setDeleting(true);
    await supabase.storage.from('invoices').remove([file.storage_path]);
    await supabase.from('files').delete().eq('id', file.id);
    setDeleting(false);
    toast.success('File deleted');
    onDeleted?.();
  };

  const kindLabel: Record<string, string> = {
    invoice: 'Invoice', warranty: 'Warranty', certificate: 'Certificate', other: 'Document'
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
        {isImage(file.mime)
          ? <Eye className="w-5 h-5 text-gray-500" />
          : isPDF(file.mime)
          ? <FileText className="w-5 h-5 text-red-500" />
          : <FileText className="w-5 h-5 text-gray-500" />
        }
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{file.file_name}</p>
        <p className="text-xs text-gray-500">{kindLabel[file.kind] ?? file.kind} · {formatDate(file.created_at)}</p>
      </div>
      <div className="flex gap-1 shrink-0">
        {(isImage(file.mime) || isPDF(file.mime)) && (
          <button
            onClick={open}
            disabled={opening}
            className="p-2 text-gray-400 hover:text-brand-600 rounded-lg hover:bg-brand-50 transition-colors"
            title="Open"
          >
            {opening ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
        <button
          onClick={download}
          className="p-2 text-gray-400 hover:text-brand-600 rounded-lg hover:bg-brand-50 transition-colors"
          title="Download"
        >
          <Download className="w-4 h-4" />
        </button>
        {canDelete && (
          <button
            onClick={del}
            disabled={deleting}
            className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
            title="Delete"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}
